import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ExternalLink } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminGroup, AdminMember } from '@/lib/api'
import { useConfirm } from '@/components/ConfirmDialog'
import { normalizeImageKitUrl } from '@lic/media-urls'

function isLightColor(hex: string): boolean {
  const h = (hex || '#3f3f46').replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 160
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

export default function GroupDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token, isGroupManager } = useAuth()
  const { confirm, dialog } = useConfirm()

  const [group, setGroup] = useState<AdminGroup | null>(
    (location.state as { group?: AdminGroup } | null)?.group ?? null
  )
  const [members, setMembers] = useState<AdminMember[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token || !id) return

    const groupPromise = group
      ? Promise.resolve(group)
      : api.getAdminGroups(token).then((items) => items.find((g) => g.id === id) ?? null)

    groupPromise.then(async (g) => {
      if (g) {
        setGroup(g)
        const memberList = await api.getAdminMembers(token)
        setMembers(memberList.filter((m) => m.group_slug === g.slug))
      }
      setLoading(false)
    })
  }, [id, token])

  const handleDelete = async () => {
    if (!group) return
    if (!await confirm(
      `"${group.name}" dan semua data terkait akan dihapus permanen dan tidak bisa dipulihkan.`,
      { title: `Hapus "${group.name}"` }
    )) return
    if (!token) return
    try {
      await api.deleteGroup(token, group.id)
      navigate('/groups')
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  if (!group) {
    return <div className="p-6"><p className="text-red-400 text-sm">Group not found.</p></div>
  }

  const light = isLightColor(group.color_primary || '#3f3f46')
  const socialEntries = Object.entries(group.social_handles ?? {}).filter(([, url]) => url)
  const activeMembers = members.filter((m) => m.is_active)
  const inactiveMembers = members.filter((m) => !m.is_active)

  return (
    <div className="p-6">
      {/* Top nav */}
      <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <ChevronLeft size={14} />
          Kembali
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/groups/${group.id}/edit`, { state: { group } })}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            Edit
          </button>
          {!isGroupManager && (
            <button
              onClick={handleDelete}
              className="text-xs text-red-400 hover:text-red-300 transition-colors px-3 py-2 rounded hover:bg-zinc-800"
            >
              Hapus
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
      {/* Hero */}
      <div
        className="relative w-full h-56 rounded-2xl overflow-hidden mb-5"
        style={{
          backgroundColor: group.color_primary || '#3f3f46',
          ...(group.cover_image_url && {
            backgroundImage: `url(${normalizeImageKitUrl(group.cover_image_url)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }),
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: group.cover_image_url
              ? `linear-gradient(to top, ${group.color_primary}ee, ${group.color_primary}66, transparent)`
              : 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
          }}
        />

        {!group.is_active && (
          <div className="absolute top-3 right-3 z-10">
            <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-black/30 backdrop-blur-sm text-white/60 border border-white/10">
              Inactive
            </span>
          </div>
        )}

        <div className="relative h-full flex flex-col justify-between p-5">
          {/* Logo top-left */}
          {group.logo_url && (
            <div>
              <img
                src={normalizeImageKitUrl(group.logo_url)}
                alt={`${group.name} logo`}
                className="h-8 w-auto object-contain drop-shadow-lg"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            </div>
          )}
          <div className={!group.logo_url ? 'mt-auto' : ''}>
            <h1 className={`text-2xl font-bold leading-tight ${light ? 'text-zinc-900' : 'text-white'}`}>
              {group.name}
            </h1>
            {group.tagline && (
              <p className={`text-sm mt-1 ${light ? 'text-zinc-700' : 'text-white/70'}`}>
                {group.tagline}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">

        {/* Stat row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Debut</p>
            <p className="text-sm text-white">{group.debut_date || '—'}</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Warna</p>
            <div className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded border border-zinc-700 flex-shrink-0"
                style={{ background: group.color_primary }}
              />
              <p className="text-xs text-white font-mono">{group.color_primary}</p>
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">Members</p>
            <p className="text-sm text-white">
              {activeMembers.length}
              {inactiveMembers.length > 0 && (
                <span className="text-zinc-600 text-xs ml-1">+{inactiveMembers.length} inactive</span>
              )}
            </p>
          </div>
        </div>

        {/* Description */}
        {group.description && (
          <Section title="Deskripsi">
            <p className="text-zinc-300 text-sm leading-relaxed">{group.description}</p>
          </Section>
        )}

        {/* Members */}
        {members.length > 0 && (
          <Section title="Members">
            <div className="grid grid-cols-4 gap-3">
              {[...activeMembers, ...inactiveMembers].slice(0, 8).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => navigate(`/members/${m.id}`, { state: { member: m } })}
                  className="flex flex-col items-center gap-1.5 group/member"
                >
                  <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-zinc-800">
                    {m.image_url ? (
                      <img
                        src={normalizeImageKitUrl(m.image_url)}
                        alt={m.stage_name}
                        className="w-full h-full object-cover object-top group-hover/member:scale-105 transition-transform duration-300"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-zinc-600 font-bold text-lg">{m.stage_name[0]}</span>
                      </div>
                    )}
                    {!m.is_active && (
                      <div className="absolute inset-0 bg-black/50" />
                    )}
                  </div>
                  <p className={`text-[11px] font-medium truncate w-full text-center ${m.is_active ? 'text-zinc-300 group-hover/member:text-white' : 'text-zinc-600'}`}>
                    {m.stage_name}
                  </p>
                </button>
              ))}
            </div>
            {members.length > 8 && (
              <button
                type="button"
                onClick={() => navigate(`/members?group=${group.slug}`)}
                className="mt-3 text-xs text-zinc-500 hover:text-yellow-400 transition-colors"
              >
                Lihat semua {members.length} member →
              </button>
            )}
          </Section>
        )}

        {/* Social */}
        {socialEntries.length > 0 && (
          <Section title="Social Media">
            <div className="space-y-2">
              {socialEntries.map(([platform, url]) => (
                <div key={platform} className="flex items-center justify-between gap-4">
                  <span className="text-[11px] font-medium text-zinc-400 w-24 flex-shrink-0 capitalize">
                    {platform}
                  </span>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-yellow-400 transition-colors truncate min-w-0"
                  >
                    <span className="truncate">{url}</span>
                    <ExternalLink size={10} className="flex-shrink-0" />
                  </a>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Meta */}
        <Section title="Info Teknis">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">Slug</p>
              <p className="text-xs text-zinc-300 font-mono">{group.slug}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-500 mb-1">ID</p>
              <p className="text-xs text-zinc-600 font-mono truncate">{group.id}</p>
            </div>
          </div>
        </Section>

      </div>
      </div>
      {dialog}
    </div>
  )
}
